import React from 'react';
import '../Editor.css'; // Import the new shared editor styles

const DialoguePointer = ({ 
  npcNames = [], 
  dialogues = {}, 
  buttons = {},
  selectedButton, 
  selectedTargetNpc, 
  collapsedNpcs = {}, 
  toggleNpcCollapse, 
  getPointedDialogues = () => ({}), 
  setCommands, 
  setSelectedButton,
  findNpcNameByTag = () => null,
  quizModeEnabled = false,
  dialogueChainEnabled = false,
  config = { npcType: 'default', sceneTracking: 'scores' },
  entityIds = {}
}) => {
  // Get the pointed dialogues map
  const pointedDialogues = getPointedDialogues();

  // Filter NPCs based on selection
  const filteredNpcs = selectedTargetNpc ? [selectedTargetNpc] : npcNames;

  // Helper functions for command generation based on configuration
  const isCustomEntity = config.npcType === 'custom';
  const isScriptBased = config.sceneTracking === 'scripts';

  const getEntitySelector = (npcName) => {
    if (isCustomEntity && entityIds[npcName]) {
      return `@e[type=${entityIds[npcName]}]`;
    } else {
      const npcTag = npcName.toLowerCase().replace(/\s+/g, '_').replace(/\W/g, '');
      return `@e[tag=${npcTag}]`;
    }
  };

  const getEntityId = (npcName) => {
    if (isCustomEntity && entityIds[npcName]) {
      return entityIds[npcName];
    } else {
      return npcName.toLowerCase().replace(/\s+/g, '_').replace(/\W/g, '');
    }
  };

  // NEW: Get scene tag - this should be consistent across all files
  const getSceneTag = (npcName) => {
    // Always use the processed NPC name (not the entity ID) for scene tags
    return npcName.toLowerCase().replace(/\s+/g, '_').replace(/\W/g, '');
  };

  const generateCommands = (targetNpc, targetDialogueIndex, extraCommands = []) => {
    const entityId = getEntityId(targetNpc);
    const entitySelector = getEntitySelector(targetNpc);
    const sceneTag = getSceneTag(targetNpc); // Use consistent scene tag
    
    if (isScriptBased) {
      // For script-based system: use entity ID for selector but scene tag for the scene name
      return [
        `/dialogue open ${entitySelector} @s ${sceneTag}.${targetDialogueIndex}`,
        `/scriptevent npc:change_scene ${entityId}|${sceneTag}.${targetDialogueIndex}`,
        ...extraCommands
      ];
    } else {
      // For score-based system: dialogue open + scoreboard + extra commands
      const scoreboardName = sceneTag; // Use scene tag for scoreboard name too
      return [
        `/dialogue open ${entitySelector} @s ${sceneTag}.${targetDialogueIndex}`,
        `/scoreboard players set @initiator ${scoreboardName} ${targetDialogueIndex}`,
        ...extraCommands
      ];
    }
  };

  // Function to find a button that belongs to a specific dialogue
  const findButtonForDialogue = (targetNpc, targetDialogueIndex) => {
    if (!targetNpc || targetDialogueIndex === undefined || !dialogues[targetNpc]) return null;
    
    const targetDialogue = dialogues[targetNpc][targetDialogueIndex];
    if (!targetDialogue || !buttons[targetNpc] || !buttons[targetNpc][targetDialogue]) return null;
    
    // Return the first button from this dialogue
    const availableButtons = buttons[targetNpc][targetDialogue];
    return availableButtons.length > 0 && availableButtons[0] !== '$placeholder_button' 
      ? { npc: targetNpc, dialogue: targetDialogue, button: availableButtons[0] }
      : null;
  };

  // Function to handle pointing to a dialogue and auto-selecting the next button (for dialogue chain mode)
  const handlePointToDialogue = (targetNpc, targetDialogueIndex) => {
    if (!selectedButton) return;

    const commands = generateCommands(targetNpc, targetDialogueIndex);

    // Add commands to point to the selected NPC's dialogue
    setCommands(prevCommands => {
      const newCommands = { ...prevCommands };
      
      // Initialize structure if needed
      if (!newCommands[selectedButton.npc]) {
        newCommands[selectedButton.npc] = {};
      }
      
      if (!newCommands[selectedButton.npc][selectedButton.dialogue]) {
        newCommands[selectedButton.npc][selectedButton.dialogue] = {};
      }
      
      if (!newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button]) {
        newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button] = [];
      }
      
      // Set the generated commands
      newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button] = commands;
      
      return newCommands;
    });

    // Show a confirmation message
    const commandType = isScriptBased ? 'script event' : 'scoreboard';
    alert(`Button now points to "${targetNpc}" dialogue #${targetDialogueIndex} using ${commandType} system`);
    
    // If dialogue chain mode is enabled, find the next button to select
    if (dialogueChainEnabled) {
      // Find the next button to select (auto-selection)
      const nextButton = findButtonForDialogue(targetNpc, targetDialogueIndex);
      
      // If found, select it, otherwise deselect current button
      setSelectedButton(nextButton);
      
      // If we found a next button, expand its NPC section
      if (nextButton) {
        // Expand the NPC section for the newly selected button
        toggleNpcCollapse(nextButton.npc, false); // force expand
      }
    } else {
      // If chain mode is not enabled, keep the current button selected
      // Do nothing with the selectedButton state
    }
  };

  // Handle correct answer in quiz mode
  const handleCorrectAnswer = (targetNpc, targetDialogueIndex) => {
    if (!selectedButton) return;
    
    const commands = generateCommands(targetNpc, targetDialogueIndex, ["/playsound random.levelup @initiator"]);

    // Add commands to point to the selected NPC's dialogue plus the success sound
    setCommands(prevCommands => {
      const newCommands = { ...prevCommands };
      
      // Initialize structure if needed
      if (!newCommands[selectedButton.npc]) {
        newCommands[selectedButton.npc] = {};
      }
      
      if (!newCommands[selectedButton.npc][selectedButton.dialogue]) {
        newCommands[selectedButton.npc][selectedButton.dialogue] = {};
      }
      
      if (!newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button]) {
        newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button] = [];
      }
      
      // Set the generated commands
      newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button] = commands;
      
      return newCommands;
    });

    // Show a confirmation message
    const commandType = isScriptBased ? 'script event' : 'scoreboard';
    alert(`CORRECT ANSWER: Button now points to "${targetNpc}" dialogue #${targetDialogueIndex} using ${commandType} system with success sound!`);
    
    // If dialogue chain mode is also enabled, find the next button
    if (dialogueChainEnabled) {
      const nextButton = findButtonForDialogue(targetNpc, targetDialogueIndex);
      setSelectedButton(nextButton);
      if (nextButton) {
        toggleNpcCollapse(nextButton.npc, false);
      }
    }
  };

  // Handle wrong answer in quiz mode
  const handleWrongAnswer = (targetNpc, targetDialogueIndex) => {
    if (!selectedButton) return;
    
    const commands = generateCommands(targetNpc, targetDialogueIndex, ["/playsound beacon.deactivate @initiator"]);

    // Add commands to point to the selected NPC's dialogue plus the wrong answer sound
    setCommands(prevCommands => {
      const newCommands = { ...prevCommands };
      
      // Initialize structure if needed
      if (!newCommands[selectedButton.npc]) {
        newCommands[selectedButton.npc] = {};
      }
      
      if (!newCommands[selectedButton.npc][selectedButton.dialogue]) {
        newCommands[selectedButton.npc][selectedButton.dialogue] = {};
      }
      
      if (!newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button]) {
        newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button] = [];
      }
      
      // Set the generated commands
      newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button] = commands;
      
      return newCommands;
    });

    // Show a confirmation message
    const commandType = isScriptBased ? 'script event' : 'scoreboard';
    alert(`WRONG ANSWER: Button now points to "${targetNpc}" dialogue #${targetDialogueIndex} using ${commandType} system with wrong answer sound!`);
    
    // If dialogue chain mode is also enabled, find the next button
    if (dialogueChainEnabled) {
      const nextButton = findButtonForDialogue(targetNpc, targetDialogueIndex);
      setSelectedButton(nextButton);
      if (nextButton) {
        toggleNpcCollapse(nextButton.npc, false);
      }
    }
  };

  return (
    <div className="editor-container dialogue-pointer-container">
      {filteredNpcs.length === 0 ? (
        <div className="empty-state">No NPCs found. Add some NPCs first or adjust your filter settings.</div>
      ) : (
        <div className="dialogue-pointer-content">
          {filteredNpcs.map(npc => {
            if (!npc) return null;
            
            // Use the collapsedNpcs state to determine if this NPC is collapsed
            const isCollapsed = collapsedNpcs[npc] || false;

            return (
              <div key={npc} className="npc-section">
                <div 
                  className="npc-header"
                  onClick={() => toggleNpcCollapse(npc)}
                >
                  <span>
                    {npc}
                    {isCustomEntity && entityIds[npc] ? ` (${entityIds[npc]})` : ''}
                  </span>
                  <span>{isCollapsed ? '▼' : '▲'}</span>
                </div>
                
                {/* Render dialogues only if NPC is not collapsed */}
                {!isCollapsed && (
                  <div className="npc-content">
                    {(dialogues[npc] || []).length === 0 ? (
                      <div className="empty-dialogue-message">No dialogues for this NPC</div>
                    ) : (
                      (dialogues[npc] || []).map((dialogue, index) => {
                        if (!dialogue) return null;
                        
                        // Check if this dialogue is pointed to by any button
                        const isPointed = pointedDialogues[npc] && pointedDialogues[npc][index];

                        return (
                          <div 
                            key={index} 
                            className={`dialogue-item ${isPointed ? 'pointed' : ''}`}
                          >
                            <div className="dialogue-row">
                              <div className="dialogue-content text-wrap" title={dialogue}>
                                <span className="dialogue-index">{index}:</span> 
                                {dialogue.length > 30 ? 
                                  `${dialogue.substring(0, 30)}...` : 
                                  dialogue}
                                {isPointed && (
                                  <span className="pointed-badge">pointed</span>
                                )}
                              </div>
                              
                              {selectedButton && (
                                <div className="dialogue-action-buttons">
                                  {/* Show different buttons based on the mode */}
                                  {quizModeEnabled ? (
                                    <>
                                      <button 
                                        className="editor-button correct-answer"
                                        onClick={() => handleCorrectAnswer(npc, index)}
                                      >
                                        Correct
                                      </button>
                                      <button 
                                        className="editor-button wrong-answer"
                                        onClick={() => handleWrongAnswer(npc, index)}
                                      >
                                        Wrong
                                      </button>
                                    </>
                                  ) : (
                                    <button 
                                      className="editor-button point"
                                      onClick={() => handlePointToDialogue(npc, index)}
                                    >
                                      Point Button
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DialoguePointer;